import { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonSearch from 'components/UI/Input/PersonSearch/PersonSearch';
import axiosInstance from 'services/axios/axios';
import { deepCloneObject } from 'store/utility';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import { addRoleToUser } from '../_api';
import { BEM_NAME_ELEMENTS } from '../_const';

const FormView = props => {
	const [sErrorMessage, setErrorMessage] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [sRows, setRows] = useState([]);
	const [sTargetUser, setTargetUser] = useState();
	const [sIsExpanded, setIsExpanded] = useState(true);
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const history = useHistory();

	/**
	 * Helper method filtering rows stored in local state against user input. Adds a property to row objects
	 * used to determine visibility of row.
	 *
	 * @param {Event} e event object raised on input change.
	 * @returns void
	 */
	const indexRows = e => {
		let rowsCopy = deepCloneObject(sRows);
		rowsCopy.forEach(row => {
			const indexed = Object.keys(row.Cells).some(
				key =>
					row.Cells[key].Value &&
					row.Cells[key].Value.toLowerCase().includes(e.target.value)
			);
			row.isDisplayed = indexed;
		});
		setRows(rowsCopy);
	};

	/**
	 * Asynchronous handler raised on user action. Sets session variables according to selected user information.
	 *
	 * @param {Object} packet object representing clicked row object.
	 *
	 * @returns void
	 */
	const onRowClickedHandler = async packet => {
		setTargetUser({ ...packet?.payload?.Cells });
		setIsExpanded(state => !state);
	};

	const invokeSubmission = async value => {
		setSubmissionStatus({
			error: false,
			submitted: false,
			isSubmitting: true,
			message: '',
		});
		await addRoleToUser({ ID: value.FI_ID.Value })
			.then(response => {
				if (response.data.payload === false) {
					let displayMessage = 'Submitted Unsuccessfully';
					if (
						response?.data?.messages &&
						response.data.messages.length > 0 &&
						response.data.messages[0].Value
					) {
						displayMessage = response.data.messages[0].Value;
					}
					setSubmissionStatus({
						error: true,
						submitted: false,
						isSubmitting: false,
						message: displayMessage,
					});
				} else {
					setSubmissionStatus({
						error: false,
						submitted: true,
						isSubmitting: false,
						message: `Submitted Successfully: roles updated for ${value.Name.Value}`,
					});
					setTargetUser();
					setIsExpanded(true);
				}
			})
			.catch(() => {
				setSubmissionStatus({
					error: true,
					submitted: false,
					isSubmitting: false,
					message: 'Submission was unsuccessful',
				});
			});
	};

	return (
		<PageWithBreadcrumbs currentPageLabel='Add Privileged User'>
			<Fragment key={props.key}>
				<Typography
					variant='h6'
					color='primary'
					align='center'
					gutterBottom
				>
					Epic Administration Privileged Users
				</Typography>
				<Typography variant='body1' align='center' gutterBottom>
					Search for a user to add the necessary privileges to access
					the Epic Administration functionality.
				</Typography>
				{sSubmissionStatus?.message && (
					<Alert
						severity={
							sSubmissionStatus?.error ? 'error' : 'success'
						}
						variant='filled'
						onClose={() => {
							setSubmissionStatus(prev => (prev.message = ''));
						}}
					>
						{sSubmissionStatus?.message}
					</Alert>
				)}
				<Stack
					sx={{ marginTop: '32px' }}
					direction='column'
					spacing={{
						xs: 0.5,
						sm: 0.5,
						md: 1,
					}}
				>
					<Accordion
						sx={{
							borderRadius: '8px',
							marginTop: '32px',
							'&:before': {
								backgroundColor: 'unset',
							},
						}}
						disableGutters
						elevation={2}
						expanded={sIsExpanded}
						onChange={() => setIsExpanded(state => !state)}
						square={false}
					>
						<AccordionSummary
							sx={{
								fontFamily: 'default',
								fontWeight: 'bold',
							}}
							expandIcon={<ExpandMoreIcon />}
						>
							Search for User
						</AccordionSummary>

						<AccordionDetails
							sx={{
								padding: '0px 8px 8px 8px',
							}}
						>
							<PersonSearch
								options={{ noChips: true }}
								dataTable={{
									onTextFieldChangedHandler: indexRows,
									onRowClickedHandler: onRowClickedHandler,
									rows: sRows.filter(
										row => row?.isDisplayed !== false
									),
								}}
								errorMessage={sErrorMessage}
								isLoading={sIsSearching}
								multiPerson={false}
								onButtonClickedHandler={payload => {
									setIsSearching(true);

									axiosInstance
										.post(
											'api/Components/PersonSearch',
											payload
										)
										.then(response => {
											switch (response.status) {
												case 200:
													switch (
														response.data
															.responseCode
													) {
														case 200:
															setRows(
																response.data
																	.payload
															);
															setErrorMessage('');
															break;
														default:
															setErrorMessage(
																response.data
																	.messages[0]
																	.Value
															);
													}
													break;
												default:
													break;
											}
										})
										.catch(error => {
											setErrorMessage(
												error &&
													error.response &&
													error.response.data
													? error.response.data
															.payload
													: error.message
											);
										})
										.finally(() => {
											setIsSearching(false);
										});
								}}
								requestTarget='REQUESTEE'
							/>
						</AccordionDetails>
					</Accordion>
					{sTargetUser && (
						<Card
							variant='outlined'
							sx={{
								width: 400,
								minHeight: 150,
								m: 1,
								flexGrow: 1,
							}}
						>
							<CardContent>
								<Typography variant='h5' component='div'>
									{sTargetUser.Name.Value}
								</Typography>
								<Typography
									sx={{ mb: 1.5 }}
									color='text.secondary'
								>
									{sTargetUser.Email.Value}
								</Typography>
								<Typography variant='body2'>
									Network ID: {sTargetUser.Network_ID.Value}
								</Typography>
								<Typography variant='body2'>
									Job Title: {sTargetUser.Job_Title.Value}
								</Typography>
								<Typography variant='body2'>
									Department: {sTargetUser.Department.Value}
								</Typography>
							</CardContent>
						</Card>
					)}

					<Button
						id={`${BEM_NAME_ELEMENTS.VIEW_SECURITY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--submit`}
						onClick={() => invokeSubmission(sTargetUser)}
						variant={'contained'}
						disabled={
							!!!sTargetUser || sSubmissionStatus?.isSubmitting
						}
					>
						{sSubmissionStatus?.isSubmitting
							? 'Submitting...'
							: 'Submit'}
					</Button>
					{sSubmissionStatus?.submitted && (
						<>
							<Button
								id={`${BEM_NAME_ELEMENTS.VIEW_SECURITY}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--home`}
								variant={'contained'}
								color={'primary'}
								onClick={() =>
									history.push('/EpicUtilities/Home')
								}
							>
								Back to Epic Administration Home
							</Button>
						</>
					)}
				</Stack>
			</Fragment>
		</PageWithBreadcrumbs>
	);
};

export default FormView;
